import { useMediaUpload } from "lib/api-media";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Col,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Button,
  Row,
  FormText,
} from "reactstrap";

const AddTokenModal = ({ 
  isOpen, 
  toggle, 
  formData, 
  handleInputChange, 
  handleSubmit, 
  isLoading 
}) => {
  const { mutate: uploadMedia, isLoading: isUploading } = useMediaUpload();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadMedia(
        { file, folderName: "token-logos" },
        {
          onSuccess: (response) => {
            handleInputChange({
              target: {
                name: "logoUrl",
                value: response.url, 
              },
            });
          },
          onError: (error) => {
            console.error("Upload failed:", error);
            handleInputChange({
              target: {
                name: "logoUrl",
                value: "",
              },
            });
          },
        }
      );
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Add New Token</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <TokenFormFields 
            formData={formData} 
            handleInputChange={handleInputChange}
            handleFileUpload={handleFileUpload}
            isUploading={isUploading}
          />
        </ModalBody>
        <ModalFooter>
          <Button 
            color="primary" 
            type="submit"
            disabled={isLoading || isUploading}
          >
            {isLoading ? "Adding..." : "Add Token"}
          </Button>{" "}
          <Button color="secondary" onClick={toggle}>
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

const TokenFormFields = ({ formData, handleInputChange, handleFileUpload, isUploading }) => {
  return (
    <>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="coinId">Coin ID *</Label>
            <Input
              type="text"
              name="coinId"
              id="coinId"
              placeholder="e.g., bitcoin"
              value={formData.coinId}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="networkId">Network ID *</Label>
            <Input
              type="text"
              name="networkId"
              id="networkId"
              placeholder="e.g., mainnet"
              value={formData.networkId}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="coinGeckoId">CoinGecko ID *</Label>
            <Input
              type="text"
              name="coinGeckoId"
              id="coinGeckoId"
              placeholder="e.g., bitcoin"
              value={formData.coinGeckoId}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="name">Token Name *</Label>
            <Input
              type="text"
              name="name"
              id="name"
              placeholder="e.g., Bitcoin"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="symbol">Symbol *</Label>
            <Input
              type="text"
              name="symbol"
              id="symbol"
              placeholder="e.g., BTC"
              value={formData.symbol}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="symbolShort">Symbol Short *</Label>
            <Input
              type="text"
              name="symbolShort"
              id="symbolShort"
              placeholder="e.g., BTC"
              value={formData.symbolShort}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="nameShort">Name Short *</Label>
            <Input
              type="text"
              name="nameShort"
              id="nameShort"
              placeholder="e.g., BTC"
              value={formData.nameShort}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="binanceName">Binance Name *</Label>
            <Input
              type="text"
              name="binanceName"
              id="binanceName"
              placeholder="e.g., BTCUSDT"
              value={formData.binanceName}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="contractAddress">Contract Address *</Label>
            <Input
              type="text"
              name="contractAddress"
              id="contractAddress"
              placeholder="0x..."
              value={formData.contractAddress}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="addressRegex">Address Regex *</Label>
            <Input
              type="text"
              name="addressRegex"
              id="addressRegex"
              placeholder="^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$"
              value={formData.addressRegex}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label for="logoUpload">Token Logo *</Label>
        <Input
          type="file"
          name="logoUpload"
          id="logoUpload"
          accept="image/*"
          onChange={handleFileUpload}
          disabled={isUploading}
        />
        <FormText>
          {isUploading ? "Uploading..." : formData.logoUrl ? `Uploaded: ${formData.logoUrl}` : "Select an image file"}
        </FormText>
      </FormGroup>

      <Row>
        <Col md="4">
          <FormGroup>
            <Label for="price">Current Price</Label>
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>$</InputGroupText>
              </InputGroupAddon>
              <Input
                type="number"
                name="price"
                id="price"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={handleInputChange}
              />
            </InputGroup>
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="precisionDeposit">Deposit Precision</Label>
            <Input
              type="number"
              name="precisionDeposit"
              id="precisionDeposit"
              min="0"
              max="18"
              value={formData.precisionDeposit}
              onChange={handleInputChange}
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label for="precisionWithdraw">Withdraw Precision</Label>
            <Input
              type="number"
              name="precisionWithdraw"
              id="precisionWithdraw"
              min="0"
              max="18"
              value={formData.precisionWithdraw}
              onChange={handleInputChange}
            />
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup check>
            <Input
              type="checkbox"
              name="isToken"
              id="isToken"
              checked={formData.isToken}
              onChange={handleInputChange}
            />
            <Label check for="isToken">
              Is Token (not native coin)
            </Label>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup check>
            <Input
              type="checkbox"
              name="isMainnet"
              id="isMainnet"
              checked={formData.isMainnet}
              onChange={handleInputChange}
            />
            <Label check for="isMainnet">
              Is Mainnet
            </Label>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

export default AddTokenModal;